import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Select,
  InputLabel
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { timeOffRequestService } from '../../services/timeOffRequestService';
import { formatDateDisplay } from '../../utils/dateUtils';

const statusColor = (status) => {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  if (status === 'cancelled') return 'default';
  return 'warning';
};

const formatRange = (row) => {
  const start = row.start_date ? formatDateDisplay(row.start_date) : '—';
  const end = row.end_date ? formatDateDisplay(row.end_date) : '—';
  if (row.request_mode === 'partial_day') {
    const hrs = row.hours != null ? `${Number(row.hours).toFixed(2)} hrs` : '';
    return `${start}${hrs ? ` · ${hrs}` : ''}`;
  }
  if (start === end) return start;
  return `${start} – ${end}`;
};

const HOUR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8].map((h) => h.toFixed(2));
const MINUTE_OPTIONS = [
  { label: '0', value: 0 },
  { label: '15', value: 0.25 },
  { label: '30', value: 0.5 },
  { label: '45', value: 0.75 }
];

const EmployeeTimeOff = () => {
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [mode, setMode] = useState('full_day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hourWhole, setHourWhole] = useState('4.00');
  const [hourFrac, setHourFrac] = useState(0);
  const [reason, setReason] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  const load = async () => {
    const [listRes, unseenRes] = await Promise.all([
      timeOffRequestService.listMine(),
      timeOffRequestService.listUnseenDecisions()
    ]);
    setItems(listRes.data || []);
    setUnseen(unseenRes.data || []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        await load();
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load time-off requests');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const resetForm = () => {
    setMode('full_day');
    setStartDate('');
    setEndDate('');
    setHourWhole('4.00');
    setHourFrac(0);
    setReason('');
    setFormOpen(false);
  };

  const handleAcknowledge = async () => {
    try {
      await timeOffRequestService.markDecisionsSeen();
      setUnseen([]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to dismiss notifications');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        request_mode: mode,
        start_date: startDate,
        reason: reason.trim() || undefined
      };
      if (mode === 'full_day') {
        body.end_date = endDate || startDate;
      } else {
        body.hours = Math.round((parseFloat(hourWhole) + Number(hourFrac)) * 100) / 100;
      }
      await timeOffRequestService.createMine(body);
      setSuccess('Time-off request submitted. Management will review it.');
      resetForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    setError('');
    try {
      await timeOffRequestService.cancelMine(id);
      setSuccess('Request cancelled.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to cancel request');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Time Off Requests
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)} disabled={formOpen}>
          Request time off
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {unseen.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={handleAcknowledge}>
              Dismiss
            </Button>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Management response
          </Typography>
          {unseen.map((row) => {
            const reviewer = [row.reviewed_by_first_name, row.reviewed_by_last_name].filter(Boolean).join(' ');
            return (
              <Typography key={row.id} variant="body2">
                Your request for <strong>{formatRange(row)}</strong> was{' '}
                <strong>{row.status}</strong>
                {reviewer ? ` by ${reviewer}` : ''}.
                {row.review_notes ? ` Note: ${row.review_notes}` : ''}
              </Typography>
            );
          })}
        </Alert>
      )}

      {formOpen && (
        <Paper sx={{ p: 2, mb: 2 }} component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600, mb: 0.5 }}>
            Time off request
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a range of dates or request hours for a specific day. All requests are subject to approval by
            your supervisor or management.
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">Request type</FormLabel>
            <RadioGroup
              row
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <FormControlLabel value="full_day" control={<Radio size="small" />} label="Full day(s)" />
              <FormControlLabel value="partial_day" control={<Radio size="small" />} label="Partial day (hours)" />
            </RadioGroup>
          </FormControl>

          {mode === 'full_day' ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <TextField
                required
                size="small"
                label="From"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                required
                size="small"
                label="To"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                required
                size="small"
                label="Day"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Hours</InputLabel>
                <Select label="Hours" value={hourWhole} onChange={(e) => setHourWhole(e.target.value)}>
                  {HOUR_OPTIONS.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Minutes</InputLabel>
                <Select label="Minutes" value={hourFrac} onChange={(e) => setHourFrac(e.target.value)}>
                  {MINUTE_OPTIONS.map((m) => (
                    <MenuItem key={m.label} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            multiline
            minRows={2}
            inputProps={{ maxLength: 250 }}
            helperText={`${reason.length}/250`}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 250))}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={resetForm} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting || !startDate}>
              {submitting ? 'Submitting…' : 'Submit request'}
            </Button>
          </Box>
        </Paper>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Submitted</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No time-off requests yet.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.submitted_at ? formatDateDisplay(row.submitted_at) : '—'}</TableCell>
                    <TableCell>{formatRange(row)}</TableCell>
                    <TableCell>{row.request_mode === 'partial_day' ? 'Partial day' : 'Full day(s)'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} />
                    </TableCell>
                    <TableCell>{row.reason || '—'}</TableCell>
                    <TableCell align="right">
                      {row.status === 'pending' ? (
                        <Button size="small" color="inherit" onClick={() => handleCancel(row.id)}>
                          Cancel
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EmployeeTimeOff;
